/// Zustand store for debug session state: mode, breakpoints, watches, metrics,
/// heatmap data, token animations, and step history. Updated reactively by
/// debug events from the BPMN engine worker.
///
/// Phase 20, Plan 01.

import { create } from "zustand";
import type {
  DebugMode,
  PauseReason,
  BreakpointInfo,
  WatchEntry,
  ExecutionMetrics,
  ElementTimingData,
  HeatmapMode,
  TokenAnimation,
} from "@/types/debug";

// ---------------------------------------------------------------------------
// Step history entry
// ---------------------------------------------------------------------------

interface StepHistoryEntry {
  elementId: string;
  variables: Record<string, unknown>;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Store state shape
// ---------------------------------------------------------------------------

interface DebugStoreState {
  /** Current debug session mode. */
  mode: DebugMode;
  /** Reason for the current pause (breakpoint vs manual stop). */
  pauseReason: PauseReason;
  /** Breakpoints by element ID. */
  breakpoints: Map<string, BreakpointInfo>;
  /** Current token position (element ID) in the active instance. */
  currentTokenPosition: string | null;
  /** Currently debugged instance key. */
  currentInstanceKey: string | null;
  /** Pinned watch expressions. */
  pinnedWatches: WatchEntry[];
  /** Aggregated execution metrics. */
  metrics: ExecutionMetrics;
  /** Heatmap visualization mode. */
  heatmapMode: HeatmapMode;
  /** Computed heatmap values per element (normalized 0-1). */
  heatmapData: Map<string, number>;
  /** Token animation playback speed (0.25 to 4.0). */
  animationSpeed: number;
  /** Active token animations. */
  tokenAnimations: TokenAnimation[];
  /** Per-element timing data from the engine. */
  elementTimingData: Map<string, ElementTimingData>;
  /** Recent step history (ring buffer, max 50). */
  stepHistory: StepHistoryEntry[];
}

interface DebugStoreActions {
  // Session lifecycle
  startDebug: (instanceKey: string) => void;
  stopDebug: () => void;
  setMode: (mode: DebugMode) => void;

  // Breakpoint management
  toggleBreakpoint: (elementId: string, bpmnElementId: string) => void;
  setConditionalBreakpoint: (elementId: string, bpmnElementId: string, expression: string) => void;
  removeBreakpoint: (elementId: string) => void;
  clearBreakpoints: () => void;

  // Watch management
  pinWatch: (variablePath: string) => void;
  unpinWatch: (variablePath: string) => void;
  updateWatchValues: (variables: Record<string, unknown>) => void;

  // Event handling
  handleDebugEvent: (eventType: string, payload: Record<string, string>) => void;

  // Heatmap
  setHeatmapMode: (mode: HeatmapMode) => void;

  // Animation
  setAnimationSpeed: (speed: number) => void;
  addTokenAnimation: (animation: TokenAnimation) => void;
  removeTokenAnimation: (id: string) => void;

  // Metrics
  resetMetrics: () => void;
  /** Reset entire store to initial state (for tests). */
  reset: () => void;
}

type DebugStore = DebugStoreState & DebugStoreActions;

// ---------------------------------------------------------------------------
// Default metrics
// ---------------------------------------------------------------------------

const defaultMetrics: ExecutionMetrics = {
  instanceCount: 0,
  activeTokens: 0,
  completedSteps: 0,
  errorCount: 0,
  avgStepDurationMs: 0,
  jobThroughput: 0,
  startTime: null,
};

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: DebugStoreState = {
  mode: "idle",
  pauseReason: null,
  breakpoints: new Map(),
  currentTokenPosition: null,
  currentInstanceKey: null,
  pinnedWatches: [],
  metrics: { ...defaultMetrics },
  heatmapMode: "off",
  heatmapData: new Map(),
  animationSpeed: 1.0,
  tokenAnimations: [],
  elementTimingData: new Map(),
  stepHistory: [],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve a dot-notation path from a JSON object. */
function resolveJsonPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/** Compute heatmap values from element timing data for a given mode. */
function computeHeatmapData(
  timingData: Map<string, ElementTimingData>,
  mode: HeatmapMode,
): Map<string, number> {
  const result = new Map<string, number>();
  if (mode === "off" || timingData.size === 0) return result;

  // Find max value for normalization
  let maxValue = 0;
  for (const [, timing] of timingData) {
    let value = 0;
    switch (mode) {
      case "frequency":
        value = timing.count;
        break;
      case "duration":
        value = timing.count > 0 ? timing.totalDurationMs / timing.count : 0;
        break;
      case "errorRate":
        value = timing.count > 0 ? timing.errorCount / timing.count : 0;
        break;
    }
    if (value > maxValue) maxValue = value;
  }

  // Normalize to 0-1
  if (maxValue === 0) return result;
  for (const [elementId, timing] of timingData) {
    let value = 0;
    switch (mode) {
      case "frequency":
        value = timing.count;
        break;
      case "duration":
        value = timing.count > 0 ? timing.totalDurationMs / timing.count : 0;
        break;
      case "errorRate":
        value = timing.count > 0 ? timing.errorCount / timing.count : 0;
        break;
    }
    result.set(elementId, value / maxValue);
  }

  return result;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useDebugStore = create<DebugStore>((set, get) => ({
  ...initialState,

  // -------------------------------------------------------------------------
  // Session lifecycle
  // -------------------------------------------------------------------------

  startDebug: (instanceKey) =>
    set({
      mode: "running",
      pauseReason: null,
      currentInstanceKey: instanceKey,
      currentTokenPosition: null,
      metrics: { ...defaultMetrics, startTime: Date.now() },
      stepHistory: [],
      tokenAnimations: [],
    }),

  stopDebug: () =>
    set({
      mode: "idle",
      pauseReason: null,
      currentTokenPosition: null,
      currentInstanceKey: null,
      tokenAnimations: [],
    }),

  setMode: (mode) => set({ mode }),

  // -------------------------------------------------------------------------
  // Breakpoint management
  // -------------------------------------------------------------------------

  toggleBreakpoint: (elementId, bpmnElementId) =>
    set((state) => {
      const breakpoints = new Map(state.breakpoints);
      if (breakpoints.has(elementId)) {
        breakpoints.delete(elementId);
      } else {
        breakpoints.set(elementId, { elementId, bpmnElementId });
      }
      return { breakpoints };
    }),

  setConditionalBreakpoint: (elementId, bpmnElementId, expression) =>
    set((state) => {
      const breakpoints = new Map(state.breakpoints);
      breakpoints.set(elementId, {
        elementId,
        bpmnElementId,
        condition: expression,
      });
      return { breakpoints };
    }),

  removeBreakpoint: (elementId) =>
    set((state) => {
      const breakpoints = new Map(state.breakpoints);
      breakpoints.delete(elementId);
      return { breakpoints };
    }),

  clearBreakpoints: () => set({ breakpoints: new Map() }),

  // -------------------------------------------------------------------------
  // Watch management
  // -------------------------------------------------------------------------

  pinWatch: (variablePath) =>
    set((state) => {
      // Avoid duplicates
      if (state.pinnedWatches.some((w) => w.variablePath === variablePath)) {
        return state;
      }
      return {
        pinnedWatches: [...state.pinnedWatches, { variablePath }],
      };
    }),

  unpinWatch: (variablePath) =>
    set((state) => ({
      pinnedWatches: state.pinnedWatches.filter((w) => w.variablePath !== variablePath),
    })),

  updateWatchValues: (variables) =>
    set((state) => ({
      pinnedWatches: state.pinnedWatches.map((watch) => ({
        ...watch,
        lastValue: resolveJsonPath(variables, watch.variablePath),
        lastUpdated: Date.now(),
      })),
    })),

  // -------------------------------------------------------------------------
  // Event handling
  // -------------------------------------------------------------------------

  handleDebugEvent: (eventType, payload) => {
    const store = get();

    switch (eventType) {
      case "breakpointHit": {
        const variables = tryParseJson(payload.variables);
        set({ mode: "paused", pauseReason: "breakpoint", currentTokenPosition: payload.element_id });
        store.updateWatchValues(variables);
        break;
      }

      case "stepCompleted": {
        const variables = tryParseJson(payload.variables);
        const stepDurationMs = parseFloat(payload.step_duration_ms) || 0;
        const activeTokens = parseInt(payload.active_tokens, 10) || 0;

        // Update current position
        set((state) => {
          const newMetrics = { ...state.metrics };
          newMetrics.completedSteps++;
          newMetrics.activeTokens = activeTokens;
          // Running average of step duration
          newMetrics.avgStepDurationMs =
            newMetrics.completedSteps === 1
              ? stepDurationMs
              : (newMetrics.avgStepDurationMs * (newMetrics.completedSteps - 1) + stepDurationMs) /
                newMetrics.completedSteps;

          // Step history (ring buffer, max 50)
          const newHistory = [
            ...state.stepHistory,
            {
              elementId: payload.element_id,
              variables,
              timestamp: Date.now(),
            },
          ];
          if (newHistory.length > 50) {
            newHistory.splice(0, newHistory.length - 50);
          }

          return {
            currentTokenPosition: payload.element_id,
            metrics: newMetrics,
            stepHistory: newHistory,
          };
        });

        store.updateWatchValues(variables);
        break;
      }

      case "elementTiming": {
        set((state) => {
          const elementTimingData = new Map(state.elementTimingData);
          elementTimingData.set(payload.element_id, {
            count: parseInt(payload.count, 10) || 0,
            totalDurationMs: parseFloat(payload.total_duration_ms) || 0,
            errorCount: parseInt(payload.error_count, 10) || 0,
          });

          // Recompute heatmap data if heatmap is active
          const heatmapData =
            state.heatmapMode !== "off"
              ? computeHeatmapData(elementTimingData, state.heatmapMode)
              : state.heatmapData;

          return { elementTimingData, heatmapData };
        });
        break;
      }

      case "executionPaused": {
        set({
          mode: "paused",
          pauseReason: "stopped",
          currentTokenPosition: payload.element_id || null,
        });
        break;
      }
    }
  },

  // -------------------------------------------------------------------------
  // Heatmap
  // -------------------------------------------------------------------------

  setHeatmapMode: (mode) =>
    set((state) => ({
      heatmapMode: mode,
      heatmapData: computeHeatmapData(state.elementTimingData, mode),
    })),

  // -------------------------------------------------------------------------
  // Animation
  // -------------------------------------------------------------------------

  setAnimationSpeed: (speed) => set({ animationSpeed: Math.max(0.25, Math.min(4.0, speed)) }),

  addTokenAnimation: (animation) =>
    set((state) => ({
      tokenAnimations: [...state.tokenAnimations, animation],
    })),

  removeTokenAnimation: (id) =>
    set((state) => ({
      tokenAnimations: state.tokenAnimations.filter((a) => a.id !== id),
    })),

  // -------------------------------------------------------------------------
  // Metrics
  // -------------------------------------------------------------------------

  resetMetrics: () => set({ metrics: { ...defaultMetrics } }),

  reset: () =>
    set({
      ...initialState,
      pauseReason: null,
      breakpoints: new Map(),
      heatmapData: new Map(),
      elementTimingData: new Map(),
      metrics: { ...defaultMetrics },
    }),
}));

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function tryParseJson(json: string | undefined): Record<string, unknown> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}
